export type LatLngLiteral = {
  lat: number
  lng: number
}

export type LatLngLike =
  | LatLngLiteral
  | {
      latitude?: number | string | null
      longitude?: number | string | null
      lat?: number | string | null
      lng?: number | string | null
      lon?: number | string | null
    }
  | null
  | undefined

export type HereRouteSummary = {
  durationSeconds: number
  lengthMeters: number
  polylines: LatLngLiteral[][]
  raw: any
}

const DEFAULT_CENTER: LatLngLiteral = {
  lat: 51.5287718,
  lng: -0.2416802,
}

const DECODING_TABLE =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

const toSigned = (value: number) => (value & 1 ? -(value + 1) / 2 : value / 2)

const decodeChar = (char: string) => {
  const value = DECODING_TABLE.indexOf(char)

  if (value === -1) {
    throw new Error(`Invalid flexible polyline character: ${char}`)
  }

  return value
}

const decodeUnsignedValue = (encoded: string, indexRef: { value: number }) => {
  let result = 0
  let shift = 0

  while (indexRef.value < encoded.length) {
    const value = decodeChar(encoded[indexRef.value])
    indexRef.value += 1
    result |= (value & 0x1f) << shift

    if ((value & 0x20) === 0) {
      return result
    }

    shift += 5
  }

  throw new Error('Invalid flexible polyline encoding')
}

export const normaliseCoordinate = (
  value: LatLngLike,
): LatLngLiteral | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const latCandidate =
    'lat' in value ? value.lat : 'latitude' in value ? value.latitude : null
  const lngCandidate =
    'lng' in value
      ? value.lng
      : 'longitude' in value
      ? value.longitude
      : 'lon' in value
      ? value.lon
      : null

  const lat = Number(latCandidate)
  const lng = Number(lngCandidate)

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null
  }

  return { lat, lng }
}

export const getDefaultCenter = () => DEFAULT_CENTER

export const buildHereTileUrl = (apiKey: string) =>
  `https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png?size=512&style=explore.day&lang=en&apiKey=${apiKey}`

export const buildHereTrafficTileUrl = (apiKey: string) =>
  `https://traffic.maps.hereapi.com/v3/flow/mc/{z}/{x}/{y}/png?size=512&style=explore.day&apiKey=${apiKey}`

export const prefetchHereVectorTiles = (
  coords: LatLngLiteral[],
  zooms: number[],
  apiKey: string,
  padding = 1,
  maxWaitMs = 2500,
): { promise: Promise<void>; abort: () => void } => {
  if (!apiKey || !coords.length || typeof fetch === 'undefined') {
    return { promise: Promise.resolve(), abort: () => {} }
  }
  const seen = new Set<string>()
  const urls: string[] = []
  for (const z of zooms) {
    const n = 2 ** z
    for (const { lat, lng } of coords) {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
      const latRad = (lat * Math.PI) / 180
      const tx = Math.floor(((lng + 180) / 360) * n)
      const ty = Math.floor(
        ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n,
      )
      for (let dx = -padding; dx <= padding; dx++) {
        for (let dy = -padding; dy <= padding; dy++) {
          const x = (((tx + dx) % n) + n) % n
          const y = ty + dy
          if (y < 0 || y >= n) continue
          const key = `${z}/${x}/${y}`
          if (seen.has(key)) continue
          seen.add(key)
          urls.push(
            `https://vector.hereapi.com/v2/vectortiles/base/mc/${z}/${x}/${y}/omv?apikey=${apiKey}`,
          )
        }
      }
    }
  }
  if (!urls.length) {
    return { promise: Promise.resolve(), abort: () => {} }
  }

  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null
  const signal = controller?.signal

  const promise = new Promise<void>((resolve) => {
    let pending = urls.length
    let done = false
    const finish = () => {
      if (done) return
      if (--pending <= 0) {
        done = true
        resolve()
      }
    }
    const timeout = setTimeout(() => {
      if (done) return
      done = true
      try {
        controller?.abort()
      } catch {
        /* swallow */
      }
      resolve()
    }, maxWaitMs)
    const wrap = () => {
      finish()
      if (done) clearTimeout(timeout)
    }
    for (const url of urls) {
      fetch(url, { mode: 'cors', credentials: 'omit', signal })
        .then(wrap)
        .catch(wrap)
    }
  })

  return {
    promise,
    abort: () => {
      try {
        controller?.abort()
      } catch {
        /* swallow */
      }
    },
  }
}

export const prefetchHereTiles = (
  coords: LatLngLiteral[],
  zooms: number[],
  apiKey: string,
  padding = 1,
  maxWaitMs = 3500,
): Promise<void> => {
  if (!apiKey || !coords.length || typeof Image === 'undefined') {
    return Promise.resolve()
  }
  const template = buildHereTileUrl(apiKey)
  const seen = new Set<string>()
  const urls: string[] = []
  for (const z of zooms) {
    const n = 2 ** z
    for (const { lat, lng } of coords) {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
      const latRad = (lat * Math.PI) / 180
      const tx = Math.floor(((lng + 180) / 360) * n)
      const ty = Math.floor(
        ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n,
      )
      for (let dx = -padding; dx <= padding; dx++) {
        for (let dy = -padding; dy <= padding; dy++) {
          const x = (((tx + dx) % n) + n) % n
          const y = ty + dy
          if (y < 0 || y >= n) continue
          const key = `${z}/${x}/${y}`
          if (seen.has(key)) continue
          seen.add(key)
          urls.push(
            template
              .replace('{z}', String(z))
              .replace('{x}', String(x))
              .replace('{y}', String(y)),
          )
        }
      }
    }
  }
  if (!urls.length) return Promise.resolve()

  return new Promise<void>((resolve) => {
    let pending = urls.length
    let done = false
    const finish = () => {
      if (done) return
      if (--pending <= 0) {
        done = true
        resolve()
      }
    }
    const timeout = setTimeout(() => {
      if (done) return
      done = true
      resolve()
    }, maxWaitMs)
    const wrap = () => {
      finish()
      if (done) clearTimeout(timeout)
    }
    for (const url of urls) {
      const img = new Image()
      img.decoding = 'async'
      img.onload = wrap
      img.onerror = wrap
      img.src = url
    }
  })
}

export const decodeFlexiblePolyline = (encoded: string): LatLngLiteral[] => {
  const indexRef = { value: 0 }
  const version = decodeUnsignedValue(encoded, indexRef)

  if (version !== 1) {
    throw new Error(`Unsupported flexible polyline version: ${version}`)
  }

  const header = decodeUnsignedValue(encoded, indexRef)
  const precision = header & 15
  const thirdDimension = (header >> 4) & 7
  const factor = 10 ** precision

  let lastLat = 0
  let lastLng = 0
  let lastZ = 0
  const coordinates: LatLngLiteral[] = []

  while (indexRef.value < encoded.length) {
    lastLat += toSigned(decodeUnsignedValue(encoded, indexRef))
    lastLng += toSigned(decodeUnsignedValue(encoded, indexRef))

    if (thirdDimension) {
      lastZ += toSigned(decodeUnsignedValue(encoded, indexRef))
      void lastZ
    }

    coordinates.push({
      lat: lastLat / factor,
      lng: lastLng / factor,
    })
  }

  return coordinates
}

export const fetchHereRoute = async ({
  apiKey,
  origin,
  destination,
  via = [],
  signal,
}: {
  apiKey: string
  origin: LatLngLiteral
  destination: LatLngLiteral
  via?: LatLngLiteral[]
  signal?: AbortSignal
}): Promise<HereRouteSummary> => {
  const params = new URLSearchParams({
    apiKey,
    transportMode: 'car',
    origin: `${origin.lat},${origin.lng}`,
    destination: `${destination.lat},${destination.lng}`,
    return: 'polyline,summary',
  })

  via.forEach((point) => {
    params.append('via', `${point.lat},${point.lng}`)
  })

  const response = await fetch(
    `https://router.hereapi.com/v8/routes?${params.toString()}`,
    { signal },
  )

  if (!response.ok) {
    throw new Error(`HERE routing failed with status ${response.status}`)
  }

  const data = await response.json()
  const route = data?.routes?.[0]

  if (!route?.sections?.length) {
    throw new Error('HERE routing returned no sections')
  }

  const durationSeconds = route.sections.reduce(
    (total: number, section: any) => total + (section?.summary?.duration ?? 0),
    0,
  )
  const lengthMeters = route.sections.reduce(
    (total: number, section: any) => total + (section?.summary?.length ?? 0),
    0,
  )
  const polylines = route.sections.map((section: any) =>
    decodeFlexiblePolyline(section.polyline),
  )

  return {
    durationSeconds,
    lengthMeters,
    polylines,
    raw: data,
  }
}

export const metersToMiles = (meters: number) => meters * 0.000621371

const haversineKm = (a: LatLngLiteral, b: LatLngLiteral): number => {
  const R = 6371
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)))
}

const cruiseZoomFor = (distanceKm: number, closeZoom = 11): number => {
  if (distanceKm <= 0) return closeZoom
  const z = closeZoom - Math.log2(Math.max(1, distanceKm / 50))
  return Math.max(3, Math.min(closeZoom, z))
}

const inflightPrefetchRoutes = new Set<string>()

export const prefetchHereFlyoverTilesForRoute = ({
  from,
  to,
  apiKey,
}: {
  from: LatLngLiteral
  to: LatLngLiteral
  apiKey: string
}): void => {
  if (!apiKey || typeof fetch === 'undefined') return
  if (
    !Number.isFinite(from.lat) ||
    !Number.isFinite(from.lng) ||
    !Number.isFinite(to.lat) ||
    !Number.isFinite(to.lng)
  ) {
    return
  }
  const dedupeKey = `${from.lat.toFixed(4)},${from.lng.toFixed(
    4,
  )}->${to.lat.toFixed(4)},${to.lng.toFixed(4)}`
  if (inflightPrefetchRoutes.has(dedupeKey)) return
  inflightPrefetchRoutes.add(dedupeKey)

  const closeZoom = 11
  const distanceKm = haversineKm(from, to)
  const midZoom = cruiseZoomFor(distanceKm, closeZoom)
  const startZoom = Math.min(closeZoom, midZoom + 3)

  const dx = to.lng - from.lng
  const dy = to.lat - from.lat
  const midLat = (from.lat + to.lat) / 2
  const midLng = (from.lng + to.lng) / 2
  const ctrlLat = midLat + -dx * 0.22
  const ctrlLng = midLng + dy * 0.22
  const bezierAt = (s: number): LatLngLiteral => {
    const om = 1 - s
    return {
      lat: om * om * from.lat + 2 * om * s * ctrlLat + s * s * to.lat,
      lng: om * om * from.lng + 2 * om * s * ctrlLng + s * s * to.lng,
    }
  }

  const densePath: LatLngLiteral[] = []
  for (let i = 0; i <= 64; i++) densePath.push(bezierAt(i / 64))

  const minZ = Math.max(2, Math.floor(midZoom))
  const maxZ = Math.max(Math.ceil(startZoom), Math.ceil(midZoom), closeZoom + 1)
  const zooms: number[] = []
  for (let z = minZ; z <= maxZ; z++) zooms.push(z)

  const startZoomFloor = Math.floor(startZoom)
  const startZoomCeil = Math.ceil(startZoom)

  const seen = new Set<string>()
  const urls: string[] = []
  const collect = (coords: LatLngLiteral[], zs: number[], padding: number) => {
    for (const z of zs) {
      const n = 2 ** z
      for (const { lat, lng } of coords) {
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
        const latRad = (lat * Math.PI) / 180
        const tx = Math.floor(((lng + 180) / 360) * n)
        const ty = Math.floor(
          ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n,
        )
        for (let dx2 = -padding; dx2 <= padding; dx2++) {
          for (let dy2 = -padding; dy2 <= padding; dy2++) {
            const x = (((tx + dx2) % n) + n) % n
            const y = ty + dy2
            if (y < 0 || y >= n) continue
            const key = `${z}/${x}/${y}`
            if (seen.has(key)) continue
            seen.add(key)
            urls.push(
              `https://vector.hereapi.com/v2/vectortiles/base/mc/${z}/${x}/${y}/omv?apikey=${apiKey}`,
            )
          }
        }
      }
    }
  }
  collect([from], [startZoomFloor, startZoomCeil], 3)
  collect([to], [closeZoom, closeZoom + 1], 3)
  collect([from, to], zooms, 2)
  collect(densePath, zooms, 1)

  const CONCURRENCY = 8
  let cursor = 0
  const runNext = (): Promise<void> => {
    if (cursor >= urls.length) return Promise.resolve()
    const url = urls[cursor++]
    return fetch(url, { mode: 'cors', credentials: 'omit' })
      .catch(() => undefined)
      .then(runNext)
  }
  const workers: Promise<void>[] = []
  for (let i = 0; i < Math.min(CONCURRENCY, urls.length); i++) {
    workers.push(runNext())
  }
  Promise.all(workers).finally(() => {
    inflightPrefetchRoutes.delete(dedupeKey)
  })
}
